<template>
  <Modal
    :active="active"
    :vehicle="vehicle"
    :title="$t('forms.privateLease.title')"
    class="privateLeaseModal"
    @modalClosed="closeModal"
    ref="modal"
  >
    <div class="form-modal-intro">
      <div v-if="introText" v-html="introText"></div>
    </div>
    <div class="progressBar">
      <div
        class="progressBar__step -clickable"
        v-on:click="toStep(1, true)"
        :class="{ '-active': currentStep === 1, '-passed': currentStep > 1 }"
      >
        {{ $t('forms.privateLease.stepCalculator') }}
      </div>
      <div class="progressBar__step" v-on:click="toStep(2, true)" :class="{ '-active': currentStep === 2, '-passed': currentStep > 2 }">
        {{ $t('forms.privateLease.stepPersonalDetails') }}
      </div>
      <div class="progressBar__step" :class="{ '-active': currentStep === 3, '-passed': currentStep > 3 }">
        {{ $t('forms.privateLease.stepConfirm') }}
      </div>
    </div>
    <!-- .privateLeaseModal__progress -->

    <div v-if="currentStep === 1" class="privateLeaseModal__page page-1">
      <div v-if="calculationsFetched && !calculationsFetchError && calculations.length" class="formGroup formGroup--transparent">
        <div class="formRow">
          <div class="formRow__label formRow__label--wide">
            <label class="form-label"
              ><span class="resultText">{{ $t('forms.privateLease.leaseAmount') }}</span></label
            >
          </div>
          <!-- .formRow__label formRow__label--wide -->
          <div class="formRow__field formRow__field--text">
            <div v-for="(calculation, index) in calculations" :key="index">
              <span class="resultText__before">{{ $t('forms.privateLease.from') }}</span>
              <span class="resultText">{{ calculation.monthly.toFixed(0) | toEUR }}</span>
              <span class="resultText__after">{{ $t('forms.privateLease.perMonth') }}</span>
              <p class="resultText__disclaimer">
                {{ $t('forms.privateLease.calculationDisclaimer', { mileage: calculation.annual_mileage, duration: calculation.range }) }}
              </p>
            </div>
          </div>
          <!-- .formRow__field -->
        </div>
        <!-- .formRow -->
      </div>
      <!-- .formGroup -->

      <div v-if="!calculationsFetched && !calculationsFetchError" class="formGroup formGroup--transparent">
        <div class="privateLeaseModal__loading"></div>
      </div>

      <div class="formGroup">
        <div class="formRow">
          <div class="formRow__heading">
            <span class="formRow__heading__title">{{ $t('forms.privateLease.calculatorTitle') }}</span>
            <p class="formRow__heading__desc">{{ $t('forms.privateLease.calculatorDesc') }}</p>
          </div>
          <!-- .formRow__heading -->
        </div>
        <!-- .formRow -->

        <div class="formRow">
          <div class="formRow__label formRow__label--wide">
            <label class="form-label">{{ $t('forms.privateLease.duration') }}</label>
          </div>
          <!-- .formRow__label formRow__label--wide -->
          <div class="formRow__field">
            <div class="rangeSlider rangeSlider--toEdge">
              <veeno
                v-model="selectedDuration"
                :handles="[selectedDuration]"
                :connect="[true, false]"
                :range="{ min: 24, max: 60 }"
                :step="12"
              />
            </div>
            <!-- .rangeSlider -->
            <span class="rangeSlider__bottomValue">{{ parseInt(selectedDuration) }} {{ $t('forms.privateLease.months') }}</span>
          </div>
          <!-- .formRow__field -->
        </div>
        <!-- .formRow -->

        <div class="formRow">
          <div class="formRow__label formRow__label--wide">
            <label class="form-label">{{ $t('forms.privateLease.mileagePerYear') }}</label>
          </div>
          <!-- .formRow__label formRow__label--wide -->
          <div class="formRow__field">
            <div class="rangeSlider rangeSlider--toEdge">
              <veeno
                v-model="selectedMileage"
                :handles="[selectedMileage]"
                :connect="[true, false]"
                :range="{ min: 5000, max: 30000 }"
                :step="5000"
              />
            </div>
            <!-- .rangeSlider -->
            <span class="rangeSlider__bottomValue"
              >{{ parseInt(selectedMileage) | thousandSeparated }} {{ $t('vehicle.mileageUnit') }}</span
            >
          </div>
          <!-- .formRow__field -->
        </div>
        <!-- .formRow -->

        <div class="formRow">
          <div class="formRow__label formRow__label--wide">
            <label class="form-label" for="leaseCalculator-damageFreeYears">{{ $t('forms.privateLease.damageFreeYears') }}</label>
          </div>
          <!-- .formRow__label -->
          <div class="formRow__field">
            <input id="leaseCalculator-damageFreeYears" class="form-textField" type="text" v-model="damageFreeYears" maxLength="10" />
          </div>
          <!-- .formRow__field -->
        </div>
        <!-- .formRow -->

        <div class="formRow">
          <div class="formRow__label formRow__label--wide">
            <label class="form-label">{{ $t('forms.privateLease.prepayment') }}</label>
          </div>
          <!-- .formRow__label formRow__label--wide -->
          <div class="formRow__field">
            <div class="rangeSlider rangeSlider--toEdge">
              <veeno
                v-model="selectedPrepayment"
                :handles="[selectedPrepayment]"
                :connect="[true, false]"
                :key="basePrice"
                :range="{ min: 0, max: Math.floor(basePrice / 50) * 50 }"
                :step="50"
              />
            </div>
            <!-- .rangeSlider -->
            <span class="rangeSlider__bottomValue">{{ selectedPrepayment | toEUR }}</span>
          </div>
          <!-- .formRow__field -->
        </div>
        <!-- .formRow -->
      </div>
      <!-- .formGroup -->

      <div class="formGroup formGroup--transparent">
        <div class="formRow">
          <div class="formRow__field formRow__field--noWideLabel">
            <button v-on:click="toStep(2)" class="button button--primary button--arrow button--submit">
              {{ $t('forms.privateLease.request') }}
            </button>
          </div>
          <!-- .formRow__field -->
        </div>
        <!-- .formRow -->
      </div>
      <!-- .formGroup -->
    </div>
    <!-- .privateLeaseModal__page -->

    <div v-if="currentStep === 2" class="privateLeaseModal__page page-2">
      <p v-if="errors.length > 0" class="form-error">
        {{ $t('forms.errors.requiredFields') }}
      </p>

      <div class="formRow">
        <div class="formRow__label">
          <label class="form-label">{{ $t('forms.fields.salutation') }}</label>
        </div>
        <!-- .formRow__label -->
        <div class="formRow__field formRow__field--radio">
          <div class="form-radio form-radio--inline">
            <input id="lease-salutation-1" class="form-radio__input" type="radio" value="Dhr." v-model="salutation" />
            <label class="form-radio__label" for="lease-salutation-1">{{ $t('forms.fields.salutationMale') }}</label>
          </div>
          <!-- .form-radio -->

          <div class="form-radio form-radio--inline">
            <input id="lease-salutation-2" class="form-radio__input" type="radio" value="Mevr." v-model="salutation" />
            <label class="form-radio__label" for="lease-salutation-2">{{ $t('forms.fields.salutationFemale') }}</label>
          </div>
          <!-- .form-radio -->

          <p v-if="isError('salutation')" class="form-fieldError">
            {{ $t('forms.errors.requiredFieldSingle') }}
          </p>
        </div>
        <!-- .formRow__field -->
      </div>
      <!-- .formRow -->

      <div class="formRow">
        <div class="formRow__label">
          <label class="form-label" for="lease-fname">{{ $t('forms.fields.name') }}</label>
        </div>
        <!-- .formRow__label -->
        <div class="formRow__field">
          <div class="formRow__field__grid">
            <div class="formRow__field__grid__column">
              <input
                id="lease-fname"
                class="form-textField"
                :class="{ 'form-textField--error': isError('fname') }"
                type="text"
                v-model="fname"
                :placeholder="$t('forms.fields.firstName')"
              />
              <p v-if="isError('fname')" class="form-fieldError">
                {{ $t('forms.errors.requiredFieldSingle') }}
              </p>
            </div>
            <!-- .formRow__field__grid__column -->
            <div class="formRow__field__grid__column">
              <input
                id="lease-lname"
                class="form-textField"
                :class="{ 'form-textField--error': isError('lname') }"
                type="text"
                v-model="lname"
                :placeholder="$t('forms.fields.lastName')"
              />
              <p v-if="isError('lname')" class="form-fieldError">
                {{ $t('forms.errors.requiredFieldSingle') }}
              </p>
            </div>
            <!-- .formRow__field__grid__column -->
          </div>
          <!-- .formRow__field__grid -->
        </div>
        <!-- .formRow__field -->
      </div>
      <!-- .formRow -->

      <div class="formRow">
        <div class="formRow__label">
          <label class="form-label" for="leaseCalculator-email">{{ $t('forms.fields.email') }}</label>
        </div>
        <!-- .formRow__label -->
        <div class="formRow__field">
          <input
            id="leaseCalculator-email"
            class="form-textField"
            :class="{ 'form-textField--error': isError('email') }"
            type="email"
            v-model="email"
          />
          <p v-if="isError('email')" class="form-fieldError">
            {{ $t('forms.errors.validEmail') }}
          </p>
        </div>
        <!-- .formRow__field -->
      </div>
      <!-- .formRow -->

      <div class="formRow">
        <div class="formRow__label">
          <label class="form-label" for="lease-zip">{{ $t('forms.fields.zip') }}</label>
        </div>
        <!-- .formRow__label -->
        <div class="formRow__field">
          <input
            id="lease-zip"
            class="form-textField"
            :class="{ 'form-textField--error': isError('zip') }"
            type="text"
            v-model="zip"
            @change="fetchAddress()"
          />
          <p v-if="isError('zip')" class="form-fieldError">
            {{ $t('forms.errors.requiredFieldSingle') }}
          </p>
        </div>
        <!-- .formRow__field -->
      </div>
      <!-- .formRow -->

      <div class="formRow">
        <div class="formRow__label">
          <label class="form-label" for="lease-houseNumber">{{ $t('forms.fields.houseNumber') }}</label>
        </div>
        <!-- .formRow__label -->
        <div class="formRow__field">
          <div class="formRow__field__grid">
            <input
              id="leaseCalculator-houseNumber"
              class="form-textField formRow__field__grid__column"
              :class="{ 'form-textField--error': isError('houseNumber') }"
              type="text"
              @change="fetchAddress()"
              v-model="houseNumber"
            />

            <input
              id="leaseCalculator-houseNumberSupplement"
              class="form-textField formRow__field__grid__column"
              :class="{
                'form-textField--error': isError('houseNumberSupplement'),
              }"
              type="text"
              :placeholder="$t('forms.fields.houseNumberSupplement')"
              v-model="houseNumberSupplement"
            />
          </div>
          <!-- .formRow__field__grid -->
          <p v-if="isError('houseNumber')" class="form-fieldError">
            {{ $t('forms.errors.requiredFieldSingle') }}
          </p>
        </div>
        <!-- .formRow__field -->
      </div>
      <!-- .formRow -->

      <div v-if="addressError && !manualAddress" class="formRow">
        <div class="formRow__field formRow__field--noLabel">
          <p class="form-fieldWarning form-fieldWarning--noMargin">
            {{ $t('forms.errors.noAddressFound.text') }}
            <button class="button button--anchor" @click="fillManuallAddress()">
              {{ $t('forms.errors.noAddressFound.button') }}
            </button>
          </p>
        </div>
        <!-- .formRow__field -->
      </div>
      <!-- .formRow -->

      <div v-if="street || manualAddress" class="formRow" :class="{ 'formRow--compact': !manualAddress }">
        <div class="formRow__label">
          <label class="form-label" for="lease-street">{{ $t('forms.fields.street') }}</label>
        </div>
        <!-- .formRow__label -->
        <div class="formRow__field" :class="{ 'formRow__field--text': !manualAddress }">
          <span v-if="!manualAddress">{{ street }}</span>
          <input
            v-if="manualAddress"
            id="lease-street"
            class="form-textField"
            :class="{ 'form-textField--error': isError('street') }"
            type="text"
            v-model="street"
          />
          <p v-if="isError('street')" class="form-fieldError">
            {{ $t('forms.errors.requiredFieldSingle') }}
          </p>
        </div>
        <!-- .formRow__field -->
      </div>
      <!-- .formRow -->

      <div v-if="city || manualAddress" class="formRow">
        <div class="formRow__label">
          <label class="form-label" for="lease-city">{{ $t('forms.fields.city') }}</label>
        </div>
        <!-- .formRow__label -->
        <div class="formRow__field" :class="{ 'formRow__field--text': !manualAddress }">
          <span v-if="!manualAddress">{{ city }}</span>
          <input
            v-if="manualAddress"
            id="lease-city"
            class="form-textField"
            :class="{ 'form-textField--error': isError('city') }"
            type="text"
            v-model="city"
          />
          <p v-if="isError('city')" class="form-fieldError">
            {{ $t('forms.errors.requiredFieldSingle') }}
          </p>
        </div>
        <!-- .formRow__field -->
      </div>
      <!-- .formRow -->

      <div class="formRow">
        <div class="formRow__label">
          <label class="form-label" for="leaseCalculator-houseNumber">{{ $t('forms.fields.phone') }}</label>
        </div>
        <!-- .formRow__label -->
        <div class="formRow__field">
          <input
            id="leaseCalculator-phone"
            class="form-textField"
            :class="{ 'form-textField--error': isError('phone') }"
            type="text"
            v-model="phone"
          />
          <p v-if="isError('phone')" class="form-fieldError">
            {{ $t('forms.errors.requiredFieldSingle') }}
          </p>
        </div>
        <!-- .formRow__field -->
      </div>
      <!-- .formRow -->

      <div class="formRow formRow--spaced">
        <div class="formRow__field formRow__field--noLabel">
          <div class="form-checkbox">
            <input id="lease-acceptTerms" class="form-checkbox__input" type="checkbox" v-model="acceptTerms" />
            <label class="form-checkbox__label" for="lease-acceptTerms">
              <span
                >{{ $t('forms.fields.acceptTermsPre') }} <a :href="termsUrl" target="_blank">{{ $t('forms.fields.terms') }}</a></span
              >
            </label>
            <p v-if="isError('acceptTerms')" class="form-fieldError form-fieldError--spaced">{{ $t('forms.errors.acceptTerms') }}</p>
          </div>
          <!-- .form-checkbox -->
        </div>
        <!-- .formRow__field -->
      </div>
      <!-- .formRow -->

      <div class="formRow">
        <div class="formRow__field formRow__field--noLabel">
          <button v-on:click="toStep(3)" class="button button--primary button--arrow button--submit">
            {{ $t('forms.privateLease.toConfirmStep') }}
          </button>
        </div>
        <!-- .formRow__field -->
      </div>
      <!-- .formRow -->
    </div>
    <!-- .privateLeaseModal__page -->

    <div v-if="currentStep === 3" class="privateLeaseModal__page page-3">
      <p v-if="errorMsg" class="form-error">{{ errorMsg }}</p>

      <p class="privateLeaseModal__page__description">
        {{ $t('forms.privateLease.confirmDesc') }}
      </p>

      <div class="formGroup formGroup--pushBottom">
        <div class="formRow__heading">
          <span class="formRow__heading__title">{{ $t('forms.privateLease.leaseDetails') }}</span>
          <button class="button button--anchor" v-on:click="toStep(1)">({{ $t('forms.privateLease.edit') }})</button>
        </div>
        <!-- .formRow__heading -->
        <div class="formRow formRow--compact">
          <div class="formRow__label">
            <span class="form-label">{{ $t('forms.privateLease.duration') }}</span>
          </div>
          <!-- .formRow__label -->
          <div class="formRow__field formRow__field--text">{{ parseInt(selectedDuration) }} {{ $t('forms.privateLease.months') }}</div>
          <!-- .formRow__field -->
        </div>
        <!-- .formRow -->
        <div class="formRow formRow--compact">
          <div class="formRow__label">
            <span class="form-label">{{ $t('forms.privateLease.mileagePerYear') }}</span>
          </div>
          <!-- .formRow__label -->
          <div class="formRow__field formRow__field--text">
            {{ parseInt(selectedMileage) | thousandSeparated }}
          </div>
          <!-- .formRow__field -->
        </div>
        <!-- .formRow -->
        <div class="formRow formRow--compact">
          <div class="formRow__label">
            <span class="form-label">{{ $t('forms.privateLease.damageFreeYears') }}</span>
          </div>
          <!-- .formRow__label -->
          <div class="formRow__field formRow__field--text">
            {{ damageFreeYears || $t('forms.fields.unknown') }}
          </div>
          <!-- .formRow__field -->
        </div>
        <!-- .formRow -->
        <div class="formRow formRow--compact">
          <div class="formRow__label">
            <span class="form-label">{{ $t('forms.privateLease.prepayment') }}</span>
          </div>
          <!-- .formRow__label -->
          <div class="formRow__field formRow__field--text">
            {{ selectedPrepayment | toEUR }}
          </div>
          <!-- .formRow__field -->
        </div>
        <!-- .formRow -->
      </div>
      <!-- .formGroup -->

      <div class="formGroup formGroup--pushBottom">
        <div class="formRow__heading">
          <span class="formRow__heading__title">{{ $t('forms.privateLease.personalDetails') }}</span>
          <button class="button button--anchor" v-on:click="toStep(2)">({{ $t('forms.privateLease.edit') }})</button>
        </div>
        <!-- .formRow__heading -->
        <div class="formRow formRow--compact">
          <div class="formRow__label">
            <span class="form-label">{{ $t('forms.fields.name') }}</span>
          </div>
          <!-- .formRow__label -->
          <div class="formRow__field formRow__field--text">
            {{ salutation === 'Dhr.' ? $t('forms.fields.salutationMale') : $t('forms.fields.salutationFemale') }} {{ fname }} {{ lname }}
          </div>
          <!-- .formRow__field -->
        </div>
        <!-- .formRow -->
        <div class="formRow formRow--compact">
          <div class="formRow__label">
            <span class="form-label">{{ $t('forms.fields.email') }}</span>
          </div>
          <!-- .formRow__label -->
          <div class="formRow__field formRow__field--text">
            {{ email }}
          </div>
          <!-- .formRow__field -->
        </div>
        <!-- .formRow -->
        <div class="formRow formRow--compact">
          <div class="formRow__label">
            <span class="form-label">{{ $t('forms.fields.address') }}</span>
          </div>
          <!-- .formRow__label -->
          <div class="formRow__field formRow__field--text">
            {{ street }} {{ houseNumber }}{{ houseNumberSupplement ? ' ' + houseNumberSupplement : '' }}, {{ zip }}, {{ city }}
          </div>
          <!-- .formRow__field -->
        </div>
        <!-- .formRow -->
        <div class="formRow formRow--compact">
          <div class="formRow__label">
            <span class="form-label">{{ $t('forms.fields.phone') }}</span>
          </div>
          <!-- .formRow__label -->
          <div class="formRow__field formRow__field--text">
            {{ phone }}
          </div>
          <!-- .formRow__field -->
        </div>
        <!-- .formRow -->
      </div>
      <!-- .formGroup -->

      <button
        v-on:click="toStep(4)"
        class="button button--primary button--arrow button--submit button--alignRight"
        :class="{ 'button--loading': loading }"
        :disabled="loading"
      >
        {{ $t('forms.fields.submitRequest') }}
      </button>
    </div>
    <!-- .privateLeaseModal__page -->
    <div v-if="currentStep === 4" class="privateLeaseModal__page page-4">
      <p v-if="successMsg" class="form-success">{{ successMsg }}</p>
    </div>
    <!-- .privateLeaseModal__page -->
  </Modal>
</template>

<style lang="scss">
@import '../../styles/variables';

.imot.privateLeaseModal {
  display: flex;
  flex-flow: row nowrap;
  align-items: flex-start;
  justify-content: center;

  .privateLeaseModal {
    &__loadingWrapper {
      min-height: 50px;
    }

    &__loading {
      background: url('../../assets/icons/loading-spinner.svg') center center no-repeat;
      background-size: auto 130%;
      width: 100%;
      height: 80px;
    }

    &__page {
      &__description {
        font-size: 15px;
        margin: 0 0 1em 0;
      }

      &.page-1 {
        .form-label {
          color: #000;
        }
      }
    }
  }

  .form-modal {
    margin-top: 5vh;
    position: relative;
    transform: none;
    top: 0;
    left: 0;
  }

  .resultText {
    font-size: 20px;
    font-weight: bold;
    color: #000;
    display: inline-block;
    margin: 0 0.125em;

    &__after,
    &__before {
      font-size: 14px;
      display: inline-block;
    }

    &__disclaimer {
      display: block;
      margin: 0.25em 0 1em 0 !important;
      font-size: 13px;
      color: $color-grey-text;
    }
  }
}
</style>

<script>
import veeno from 'veeno' // eslint-disable-line
import Modal from '@/components/Modal'
import { WPSettings } from '@/WPSettings'
import axios from 'axios'

export default {
  name: 'modalLeaseCalculator',
  data() {
    return {
      WPSettings: WPSettings,
      loading: false,
      errors: [],
      errorMsg: '',
      successMsg: '',
      currentStep: 1,
      generalSettings: this.$store.state.settings.general,

      // Calculations
      calculations: [],
      calculationsFetchError: false,
      calculationsFetched: false,

      // Address fetch
      debounceTimer: null,
      addressError: false,
      manualAddress: false,

      // Form
      selectedDuration: 60,
      selectedMileage: 10000,
      selectedPrepayment: 0,
      damageFreeYears: '',
      salutation: '',
      fname: '',
      lname: '',
      email: '',
      zip: '',
      street: '',
      houseNumber: '',
      houseNumberSupplement: '',
      city: '',
      phone: '',

      acceptTerms: false,
      termsUrl: this.$store.state.settings.general.termsUrl,
    }
  },
  computed: {
    vehiclePrice() {
      const currentVehicle = this.$store.getters['vehicles/current']
      return currentVehicle.properties.price
    },
    basePriceExclTax() {
      const currentVehicle = this.$store.getters['vehicles/current']
      const price = currentVehicle.properties.price

      return currentVehicle.properties.priceInclTax ? price / 1.21 : price
    },
    basePrice() {
      return this.taxIncluded ? this.basePriceExclTax * 1.21 : +this.basePriceExclTax
    },
    currentVehicleId() {
      const currentVehicle = this.$store.getters['vehicles/current']
      return currentVehicle ? currentVehicle.id : null
    },
    licensePlate() {
      const currentVehicle = this.$store.getters['vehicles/current']
      return currentVehicle.properties.license
    },
  },
  watch: {
    taxIncluded() {
      this.offerCalculated = false
    },
    selectedDuration() {
      this.offerCalculated = false
    },
    selectedMileage() {
      this.offerCalculated = false
    },
    selectedPrepayment() {
      this.offerCalculated = false
    },
  },
  props: ['active', 'introText', 'vehicle'],
  components: {
    Modal,
    veeno,
  },
  created() {
    this.fetchCalculations()
  },
  methods: {
    toStep(step, onlyBack = false) {
      if (step > this.currentStep) {
        if (onlyBack) {
          return
        }

        if (step > 2) {
          this.validateForm()

          if (this.errors.length) {
            this.currentStep = 2
            return
          }
        }

        if (step > 3) {
          this.submitForm()
          return
        }
      }
      this.errorMsg = ''
      this.successMsg = ''
      this.currentStep = step
    },
    fetchCalculations() {
      axios
        .get(
          WPSettings.API_URL + '/get-lease-calculations',
          {
            params: {
              vehicleId: this.currentVehicleId,
            },
          },
          {
            headers:
              process.env.NODE_ENV === 'production'
                ? {
                    'X-WP-Nonce': WPSettings.NONCE,
                  }
                : {},
          }
        )
        .then((response) => {
          this.calculations = response.data.calculations
        })
        .catch(() => {
          this.calculationsFetchError = true
        })
        .finally(() => {
          this.calculationsFetched = true
        })
    },
    validateForm() {
      // Reset errors
      this.errors = []

      // Fields to check
      const checkIfHasValue = ['salutation', 'fname', 'lname', 'email', 'zip', 'street', 'houseNumber', 'city', 'phone', 'acceptTerms']

      // Check fields
      checkIfHasValue.forEach((field) => {
        if (!this[field]) {
          // Field has no value, so add it to error list
          this.errors.push(field)
        } else if (field === 'email') {
          // Test email formatting
          const regexp =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

          if (!regexp.test(this[field].toLowerCase())) {
            this.errors.push(field)
          }
        }
      })
    },
    isError(field) {
      // Check if field is in the error list
      return this.errors.includes(field)
    },
    closeModal() {
      // Reset steps
      this.currentStep = 1

      // Emit to parent component
      this.$emit('modalClosed', true)

      // Reset errors - just because it's prettier
      this.errors = []
    },
    fetchAddress() {
      // Format field values
      if (this.zip) this.zip = this.zip.replace(' ', '').toUpperCase()
      if (this.houseNumber) {
        const convertHouseNumber = parseInt(this.houseNumber)
        this.houseNumber = !isNaN(convertHouseNumber) ? convertHouseNumber : ''
      }

      // Check if values exist
      if (!this.zip || !this.houseNumber) {
        return
      }

      // Clear current running timer
      clearTimeout(this.debounceTimer)

      // Start a new debounce timer
      this.debounceTimer = setTimeout(() => {
        axios
          .get(
            WPSettings.API_URL + '/get-address',
            {
              params: {
                number: this.houseNumber,
                zip: this.zip,
              },
            },
            {
              headers:
                process.env.NODE_ENV === 'production'
                  ? {
                      'X-WP-Nonce': WPSettings.NONCE,
                    }
                  : {},
            }
          )
          .then((response) => {
            this.city = response.data.city
            this.street = response.data.street
            this.manualAddress = false
            this.addressError = false

            // Remove from error list
            this.errors = this.errors.filter((item) => item !== 'city' && item !== 'street')
          })
          .catch(() => {
            this.addressError = true
            this.city = this.street = ''
          })
      }, 500)
    },
    fillManuallAddress() {
      this.manualAddress = true
    },
    submitForm() {
      // Do the call to WP REST
      this.loading = true

      axios
        .post(
          WPSettings.API_URL + '/form-private-lease',
          {
            acceptTerms: this.acceptTerms ? true : null,
            vehicleId: this.currentVehicleId,
            duration: this.selectedDuration,
            mileage: this.selectedMileage,
            prepayment: this.selectedPrepayment,
            damageFreeYears: this.damageFreeYears || this.$t('forms.fields.unknown'),
            salutation: this.salutation,
            fname: this.fname,
            lname: this.lname,
            email: this.email,
            phoneNumber: this.phone,
            street: this.street,
            houseNumber: this.houseNumber,
            houseNumberSupplement: this.houseNumberSupplement,
            zip: this.zip,
            city: this.city,
          },
          {
            headers:
              process.env.NODE_ENV === 'production'
                ? {
                    'X-WP-Nonce': WPSettings.NONCE,
                  }
                : {},
          }
        )
        .then((response) => {
          if (response.data.status === 'success') {
            // Update success message
            this.errorMsg = ''
            this.successMsg = response.data.message
            this.currentStep = 4

            // Reset form
            this.damageFreeYears =
              this.salutation =
              this.fname =
              this.lname =
              this.email =
              this.phone =
              this.street =
              this.houseNumber =
              this.houseNumberSupplement =
              this.zip =
              this.city =
              this.acceptTerms =
                ''
          }
        })
        .catch((error) => {
          if (error.response.data.status === 'error') {
            // Update error message
            this.successMsg = ''
            this.errorMsg = error.response.data.message
          } else {
            this.successMsg = ''
            this.errorMsg = this.$t('forms.errors.somethingWentWrong')
          }
        })
        .finally(() => (this.loading = false))
    },
  },
}
</script>
